import {
  CSSProperties,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandLoader } from "@/components/brand";

/**
 * A plain-text editing surface for prose fields.
 *
 * These fields — questionnaire answers, template section bodies, expert
 * narratives — are prose that agents read and that the docx engine turns into a
 * document. They were edited in a rich text editor that stored HTML, which meant
 * every one of them had two representations that could disagree. Now there is
 * only the text.
 *
 * Templates carry `[placeholder]` tokens, so the placeholders are highlighted
 * behind the text: a mirrored, scroll-synced layer under a transparent textarea.
 */

type ProseTextAreaProps = {
  text?: string;
  /**
   * Called on every keystroke. Matches the old editor's two-argument shape so
   * callers that still persist a second field don't have to be rewritten; the
   * first argument is the same text, not markup.
   */
  updateText?: (text: string, plainText: string) => void;
  /**
   * Called on blur, and from the save button when one is shown. Blur is the save
   * point the template editors have always relied on, so it stays the save point.
   */
  saveText?: (text: string, plainText: string) => void | Promise<void>;
  shouldShowSaveBtn?: boolean;
  isLoading?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  /** "auto" grows the field to fit its content; anything else is a fixed height. */
  customHeight?: string;
  /** Highlight `[placeholder]` tokens. */
  enableBracketHighlighting?: boolean;
};

const PLACEHOLDER_PATTERN = /(\[[^[\]\n]{0,120}\])/g;

const escapeHtml = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const highlightPlaceholders = (value: string) =>
  escapeHtml(value).replace(
    PLACEHOLDER_PATTERN,
    '<mark class="bg-warn-soft text-warn rounded-xs">$1</mark>'
  );

/**
 * The mirror and the textarea must lay text out identically to the pixel, or
 * the highlight drifts away from the words it marks. Sharing one style object
 * is what guarantees that.
 */
const SHARED_TEXT_STYLES: CSSProperties = {
  fontFamily: "inherit",
  fontSize: "14px",
  lineHeight: "1.6",
  padding: "12px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word"
};

export const ProseTextArea = ({
  text = "",
  updateText,
  saveText,
  shouldShowSaveBtn = false,
  isLoading = false,
  readOnly = false,
  placeholder,
  customHeight = "auto",
  enableBracketHighlighting = false
}: ProseTextAreaProps) => {
  const [value, setValue] = useState(text);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const savedValueRef = useRef(text);
  const isAutoHeight = customHeight === "auto";

  // Server-side changes (extraction filling a field in, a draft regenerating)
  // have to win, but only when the user isn't the source of the change.
  useEffect(() => {
    setValue((current) => (current === text ? current : text));
    savedValueRef.current = text;
  }, [text]);

  useLayoutEffect(() => {
    const node = textareaRef.current;
    if (!node || !isAutoHeight) return;
    node.style.height = "auto";
    node.style.height = `${Math.max(node.scrollHeight, 120)}px`;
  }, [value, isAutoHeight]);

  const highlighted = useMemo(
    // The trailing newline keeps the mirror's last line from collapsing, which
    // would otherwise let the highlight drift above the caret.
    () =>
      enableBracketHighlighting ? highlightPlaceholders(`${value}\n`) : "",
    [value, enableBracketHighlighting]
  );

  const handleChange = (next: string) => {
    setValue(next);
    updateText?.(next, next);
  };

  const handleBlur = () => {
    if (!saveText || value === savedValueRef.current) return;
    savedValueRef.current = value;
    saveText(value, value);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-30 w-full items-center justify-center">
        <BrandLoader size="md" />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="relative w-full overflow-hidden rounded-md border border-line-3 bg-elevated">
        {enableBracketHighlighting ? (
          <div
            ref={highlightRef}
            aria-hidden
            className="pointer-events-none absolute inset-0 overflow-hidden"
            style={SHARED_TEXT_STYLES}
            dangerouslySetInnerHTML={{ __html: highlighted }}
          />
        ) : null}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(event) => handleChange(event.target.value)}
          onBlur={handleBlur}
          onScroll={(event) => {
            if (!highlightRef.current) return;
            highlightRef.current.scrollTop = event.currentTarget.scrollTop;
          }}
          readOnly={readOnly}
          placeholder={placeholder}
          className="relative w-full border-none bg-transparent text-fg outline-hidden placeholder:text-fg-4 focus:outline-hidden"
          style={{
            ...SHARED_TEXT_STYLES,
            caretColor: "var(--fg)",
            resize: isAutoHeight ? "none" : "vertical",
            minHeight: isAutoHeight ? "120px" : customHeight,
            height: isAutoHeight ? "auto" : customHeight,
            // The mirror underneath supplies the visible glyphs, so the real
            // text has to disappear while the caret stays.
            ...(enableBracketHighlighting
              ? { color: "transparent", WebkitTextFillColor: "transparent" }
              : {})
          }}
        />
      </div>

      {shouldShowSaveBtn && saveText ? (
        <div className="mt-2 flex justify-end">
          <Button variant="secondary" onClick={() => saveText(value, value)}>
            <Save />
            Save
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default ProseTextArea;
